/**
 * Processor Orders Page Component
 * Main orders view for Processor role with status tabs and search.
 * 
 * V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
 * 
 * Extracted from App.js for better code organization.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { dedupedGet } from '../lib/requestDedupe';
import { formatDateOnlyET } from '../utils/formatters';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { formatFullAddress, formatCounty, formatState } from '../utils/textFormat';
import { normalizeBorrowerName } from '../utils/nameNormalizer';
import { isProcessorRole, isTaxProduct } from '../utils/orderHelpers';
import { useWorkspacePresentation } from '../hooks/useWorkspacePresentation';
import OpsIntroOverlay from '../components/dashboard/OpsIntroOverlay';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';

// Custom Components
import { useAuth, OrderDeskEditModal } from '../App';
import { StatusBadge } from '../components/StatusBadge';
import MlsDualStatusBadges from '../components/mls/MlsDualStatusBadges';
import { PriorityIndicator } from '../components/PriorityIndicator';
import { ProcessorKPICards } from '../components/processor/ProcessorKPICards';
import OrderSummaryPopup from '../components/OrderSummaryPopup';
import { useResizableColumns } from '../hooks/useResizableColumns';
import { ResizableTableHead, ResizeGuide } from '../components/ResizableTable';
import OperationalDashboard from '../components/OperationalDashboard';
import ScrollToTopButton from '../components/ui/ScrollToTopButton';
import { FaxIndicator } from '../components/orders/FaxIndicator';
import { EmailIndicator } from '../components/orders/EmailIndicator';
import ClarificationSentIndicator from '../components/orders/ClarificationSentIndicator';
import { ProductWithCode, productDisplayText } from '../components/orders/ProductWithCode';
import { OrderScopeFilters, OrderScopeChips } from '../components/orders/management/OrderScopeFilters';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

/**
 * ProcessorOrders Component
 * Main page for processors to view and manage their assigned orders
 */
const ProcessorOrders = () => {
  const { user } = useAuth();
  // RBAC-WS-SEARCH-001: a matrix-granted Title Search workspace (e.g. QC Specialist)
  // renders exactly like a native search processor. Backend RBAC stays authoritative.
  const { presentation, isProcessorWorkspaceView, activeWorkspace } = useWorkspacePresentation(user);
  const viewRole = isProcessorWorkspaceView && !isProcessorRole(user?.role) ? presentation : user?.role;
  const matrixStatsView = isProcessorWorkspaceView && !isProcessorRole(user?.role)
    ? (activeWorkspace?.id === 'tax_processor' ? 'tax_search' : 'title_search') : null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // ============================================================================
  // URL STATE PERSISTENCE - Read initial values from URL params
  // ============================================================================
  const getInitialFromURL = (param, defaultValue) => {
    const value = searchParams.get(param);
    return value !== null ? value : defaultValue;
  };
  
  // Function to update URL params without navigation
  const updateURLParams = useCallback((updates) => {
    const newParams = new URLSearchParams(searchParams);
    
    Object.entries(updates).forEach(([key, value]) => {
      // Remove param if it's the default value
      const defaults = {
        status: 'all',
        search: '',
        orderedWithin: '12mo',
        sortBy: 'orderedOn',
        sortDir: 'desc'
      };
      
      if (value === defaults[key] || value === '' || value === null || value === undefined) {
        newParams.delete(key);
      } else {
        newParams.set(key, value);
      }
    });
    
    // Update URL without triggering navigation/reload
    setSearchParams(newParams, { replace: true });
  }, [searchParams, setSearchParams]);
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState(new Date());
  const [currentPage, setCurrentPage] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [activeTab, setActiveTab] = useState(() => getInitialFromURL('status', 'all'));
  const { searchInput: searchInputDisplay, setSearchInput: setSearchInputDisplay, debouncedValue: searchQuery, setDebouncedValue: setSearchQueryDirect, clearSearch: clearSearchState } = useDebouncedSearch(getInitialFromURL('search', ''), 400);
  const [orderedWithin, setOrderedWithin] = useState(() => getInitialFromURL('orderedWithin', '12mo'));
  // ORDER-FILTERS-001: Client Code / Product filters (URL-persisted) + facet counts from the list response
  const [scopeFilters, setScopeFilters] = useState(() => ({
    client_code: searchParams.get('client_code') || '',
    client_product: searchParams.get('product') || '',
  }));
  const scopeFiltersRef = useRef(null);
  if (scopeFiltersRef.current === null) scopeFiltersRef.current = { ...scopeFilters };
  const [scopeFacets, setScopeFacets] = useState(null);
  const handleScopeFilterChange = (next) => {
    const merged = { ...scopeFiltersRef.current, ...next };
    scopeFiltersRef.current = merged;
    setScopeFilters(merged);
  };
  const [sortBy, setSortBy] = useState(() => getInitialFromURL('sortBy', 'orderedOn'));
  const [sortDir, setSortDir] = useState(() => getInitialFromURL('sortDir', 'desc'));
  
  // Order Summary Popup state
  const [orderSummaryPopup, setOrderSummaryPopup] = useState({ isOpen: false, orderData: null });

  // Feb 2026 — DELIVERY-EDIT-RBAC-001 — Edit modal state for Admin/TL.
  const [editModalOrder, setEditModalOrder] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  
  // Dashboard stats state — null until the first payload arrives so the KPI
  // cards render skeletons instead of misleading zeros (DASH-SKELETON-001).
  const [dashboardStats, setDashboardStats] = useState(null);

  const initialProcessorColumns = [
    { id: 'order_number',   title: 'Order #',          width: '160px', minWidth: '150px', maxWidth: '180px', sortable: true, fixed: true },
    // Feb 25, 2026: Order Date column inserted after Order # (consistent
    // with the Admin/TL view). Assignee column REMOVED for Processor —
    // not relevant to processors per role-based visibility spec.
    { id: 'order_date',     title: 'Order Date',       width: '110px', minWidth: '100px', maxWidth: '130px', sortable: true, fixed: true },
    { id: 'client_code',    title: 'Client Code',      width: '90px',  minWidth: '85px',  maxWidth: '110px', sortable: true, fixed: true },
    { id: 'client_product', title: 'Client Product',   width: '110px', minWidth: '100px', maxWidth: '130px', sortable: true, fixed: true },
    { id: 'property_address', title: 'Property Address', width: '200px', minWidth: '160px', maxWidth: '260px', sortable: true, grow: 1 },
    { id: 'county',         title: 'County',           width: '100px', minWidth: '90px',  maxWidth: '120px', sortable: true, fixed: true },
    { id: 'state',          title: 'State',            width: '55px',  minWidth: '50px',  maxWidth: '65px',  sortable: true, fixed: true },
    { id: 'status',         title: 'Status',           width: '165px', minWidth: '160px', maxWidth: '185px', sortable: true, fixed: true }
  ];

  // Initialize resizable columns
  const {
    columns: processorColumns,
    visibleColumns: visibleProcessorColumns,
    columnVisibility: processorColumnVisibility,
    isResizing: isProcessorTableResizing,
    resizingColumn: resizingProcessorColumn,
    tableRef: processorTableRef,
    handleResizeStart: handleProcessorResizeStart,
    handleAutoFit: handleProcessorAutoFit,
    toggleColumnVisibility: toggleProcessorColumnVisibility,
    handleKeyboardResize: handleProcessorKeyboardResize,
    resetColumns: resetProcessorColumns
  } = useResizableColumns('processor', initialProcessorColumns);

  // May 1, 2026 — bumped page size from 25 → 100 to match the Admin /
  // Title TL dashboard. The backend `/api/processor/orders` endpoint
  // has no explicit upper bound on `pageSize`, so 100 is safe.
  const pageSize = 100;

  // Role-based feature flags
  const featuresByRole = {
    processor: {
      showAssigneeColumn: true,
      showDateRangeFilter: false,
    },
    search_processor: {
      showAssigneeColumn: false,
      showDateRangeFilter: false,
    },
    order_desk: {
      showAssigneeColumn: true,
      showDateRangeFilter: true,
    },
    default: {
      showAssigneeColumn: true,
      showDateRangeFilter: true,
    },
  };

  const features = featuresByRole[viewRole] ?? featuresByRole.default;

  // Status tabs configuration
  // V1 REQUIREMENT: Use SAME global status tabs across all roles
  // Only exclude: Mail-Away, Mail-Away Received for search roles
  const getStatusTabsForRole = (role) => {
    // Roles that should NOT see Mail-Away related tabs
    const searchRoles = ['search_processor', 'title_search_team_leader'];
    const isSearchRole = searchRoles.includes(role);
    // TYPING-SCOPE-001 (Aug 2026): Typing is part of the TITLE SEARCH
    // workflow ONLY. Tax-side roles must never see the Typing tab.
    const taxRoles = ['processor', 'tax_processor', 'tax_search_team_leader'];
    const isTaxRole = taxRoles.includes(role);
    
    // Global status tabs (same across TitleFlow)
    const globalTabs = [
      { value: 'all', label: 'All', color: 'text-gray-700' },
      { value: 'open', label: 'Open', color: 'text-blue-600' },
      { value: 'pending', label: 'Pending', color: 'text-amber-600' },
      // Feb 2026 — QC Workflow Phase 1: dedicated tab so processors see
      // soft-returned orders distinctly from regular Pending. DB status
      // value is still `pending_correction`; user-facing label is
      // "Correction Required" to match the badge, KPI, and feedback banner.
      { value: 'pending_correction', label: 'Correction Required', color: 'text-rose-600' },
      { value: 'qc_review', label: 'QC Review', color: 'text-orange-600' },
      { value: 'typing', label: 'Typing', color: 'text-cyan-600' },
      { value: 'clarification', label: 'Clarification', color: 'text-yellow-600' },
      { value: 'clarification_received', label: 'Clarification Received', color: 'text-purple-600' },
      { value: 'mailaway', label: 'Mail-Away', color: 'text-cyan-600' },
      { value: 'mailaway_received', label: 'Mail-Away Received', color: 'text-indigo-600' }
      // NOTE: Complete and Cancelled tabs removed from Dashboard (operational work queue)
      // These statuses are only viewable in Reports (historical + audit record)
    ];
    
    const excludedTabs = [];
    // For search roles, exclude Mail-Away tabs
    if (isSearchRole) excludedTabs.push('mailaway', 'mailaway_received');
    // Tax workflow never includes Typing
    if (isTaxRole) excludedTabs.push('typing');
    if (excludedTabs.length) {
      return globalTabs.filter(tab => !excludedTabs.includes(tab.value));
    }
    
    return globalTabs;
  };

  const statusTabs = getStatusTabsForRole(viewRole);

  // Processor status options for dropdown
  const processorStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'clarification', label: 'Clarification' },
    { value: 'qc_review', label: 'QC Review' },
    { value: 'complete', label: 'Complete' },
  ];

  // State machine for processor transitions
  const getAllowedProcessorTransitions = (currentStatus) => {
    const transitionMap = {
      'open': ['pending', 'clarification', 'qc_review', 'complete'],
      'pending': ['clarification', 'qc_review', 'complete'],
      'clarification': ['pending', 'qc_review', 'complete'],
      'qc_review': ['pending', 'clarification', 'complete'],
      'complete': [],
      'cancelled': []
    };
    return transitionMap[currentStatus] || [];
  };

  // Fetch orders function
  const fetchOrders = async (isBackgroundRefresh = false) => {
    return fetchOrdersWithParams(searchQuery, activeTab, orderedWithin, sortBy, sortDir, isBackgroundRefresh);
  };
  
  // Fetch orders with explicit params (used for initial load with URL params)
  const fetchOrdersWithParams = async (search, status, ordered, sort, dir, isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) {
        setLoading(true);
      } else {
        setIsRefreshing(true);
      }
      
      const params = new URLSearchParams({
        status: status,
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        orderedWithin: ordered,
        sortBy: sort,
        sortDir: dir
      });

      if (search && search.trim()) {
        params.append('q', search.trim());
      }

      // ORDER-FILTERS-001: scope filters (ref — always fresh) + facet counts from the same query
      if (scopeFiltersRef.current.client_code) params.append('client_code', scopeFiltersRef.current.client_code);
      if (scopeFiltersRef.current.client_product) params.append('client_product', scopeFiltersRef.current.client_product);
      params.append('facets', 'true');

      const response = await axios.get(`${API}/processor/orders?${params}`);
      
      setOrders(response.data.items);
      setTotalOrders(response.data.total);
      if (response.data.facets) setScopeFacets(response.data.facets);
      setLastRefreshTime(new Date());
      
    } catch (error) {
      console.error('Error fetching processor orders:', error);
      if (!isBackgroundRefresh) {
        toast.error('Failed to load orders');
      }
    } finally {
      if (!isBackgroundRefresh) {
        setLoading(false);
      } else {
        setIsRefreshing(false);
      }
    }
  };

  // Fetch dashboard stats
  const fetchDashboardStats = async () => {
    try {
      // RBAC-WS-SEARCH-001: matrix-granted workspace asks for the processor-scoped payload
      const statsUrl = matrixStatsView ? `${API}/dashboard/stats?view=${matrixStatsView}` : `${API}/dashboard/stats`;
      const response = await dedupedGet(statsUrl);
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      // First fetch failed: leave skeleton behind and fall back to zeros.
      setDashboardStats(prev => prev ?? {});
    }
  };

  // Scroll to top on component mount (e.g., after navigation back to dashboard)
  // This ensures users see the top of the dashboard after status updates
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  // Initial load and polling
  useEffect(() => {
    // Read URL params directly on initial load to ensure persistence works
    const initialSearch = searchParams.get('search') || '';
    const initialStatus = searchParams.get('status') || 'all';
    const initialOrderedWithin = searchParams.get('orderedWithin') || '12mo';
    const initialSortBy = searchParams.get('sortBy') || 'orderedOn';
    const initialSortDir = searchParams.get('sortDir') || 'desc';
    
    // Sync state with URL params on mount
    if (initialSearch !== searchQuery) {
      setSearchInputDisplay(initialSearch);
      setSearchQueryDirect(initialSearch);
    }
    if (initialStatus !== activeTab) setActiveTab(initialStatus);
    if (initialOrderedWithin !== orderedWithin) setOrderedWithin(initialOrderedWithin);
    if (initialSortBy !== sortBy) setSortBy(initialSortBy);
    if (initialSortDir !== sortDir) setSortDir(initialSortDir);
    
    fetchDashboardStats();
    // Fetch with URL params to ensure filtered results on page load
    fetchOrdersWithParams(initialSearch, initialStatus, initialOrderedWithin, initialSortBy, initialSortDir, false);
    
    const statsInterval = setInterval(fetchDashboardStats, 15000);
    const ordersInterval = setInterval(() => fetchOrders(true), 30000);
    
    return () => {
      clearInterval(statsInterval);
      clearInterval(ordersInterval);
    };
  }, [searchParams]); // Intentionally only depends on searchParams for initial load

  // Debounced search - triggers on search/filter changes and updates URL
  useEffect(() => {
    // Skip initial render
    const isInitialMount = !searchQuery && activeTab === 'all' && orderedWithin === '12mo';
    
    const timeoutId = setTimeout(() => {
      // Reset to page 1 without triggering separate effect
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
      
      // Update URL params for persistence
      updateURLParams({
        search: searchQuery,
        status: activeTab,
        orderedWithin: orderedWithin,
        sortBy: sortBy,
        sortDir: sortDir,
        client_code: scopeFilters.client_code,
        product: scopeFilters.client_product
      });
      
      // Use background refresh style to avoid flicker during search
      fetchOrders(true);
    }, 400); // Slightly longer debounce to reduce API calls
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, activeTab, orderedWithin, sortBy, sortDir, scopeFilters]);

  // Page change effect - only triggers on explicit page changes
  useEffect(() => {
    // Only fetch if we're not on page 1 (page 1 is handled by search effect)
    // This prevents double-fetch when search resets page to 1
    if (currentPage > 1) {
      fetchOrders(false);
    }
  }, [currentPage]);

  // Handle sorting - 3-click cycle: asc -> desc -> reset (default)
  // Columns whose natural reading direction is newest/largest-first.
  // Click on these columns defaults to descending so the user
  // immediately sees the most recent / highest values, matching the
  // initial dashboard load. (Text/code columns default to ascending.)
  const DESC_FIRST_COLUMNS = ['orderedOn', 'dueBy'];

  const handleSort = (column) => {
    // Bug-fix policy (May 6, 2026 — second sort regression on this
    // dashboard). The previous 3-click cycle (asc → desc → reset)
    // had two operational problems:
    //   1. The "reset" branch sets sortBy='orderedOn' / sortDir='desc'
    //      — which is identical to the dashboard's INITIAL state. So
    //      the very first click on Order Date (the default column)
    //      transitioned from {orderedOn, desc} to {orderedOn, desc}:
    //      a no-op. The user clicked, nothing happened, and the bug
    //      report rightly called it "doesn't work".
    //   2. Switching to a brand-new column always started with `asc`,
    //      so users clicking Order Date after another column saw
    //      OLDEST orders first instead of newest — counter-intuitive
    //      for a date column.
    //
    // The new contract is the conventional 2-state toggle used by
    // every modern data grid:
    //   • Same column clicked → flip direction (asc ↔ desc).
    //   • New column clicked  → use that column's natural default
    //     (`desc` for dates, `asc` for everything else).
    // No more "reset" state — if users want the default sort, they
    // refresh or click another column. Predictable, no no-op clicks.
    setCurrentPage(1);
    if (sortBy === column) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
      return;
    }
    setSortBy(column);
    setSortDir(DESC_FIRST_COLUMNS.includes(column) ? 'desc' : 'asc');
  };
  
  // Get current sort direction for a column (for header indicators)
  const getSortDirection = (column) => {
    if (sortBy === column) {
      return sortDir;
    }
    return null;
  };

  // Handle order click
  const handleOrderClick = (orderId) => {
    const order = orders.find(o => o.id === orderId);

    // Feb 25, 2026 — Modal parity for ALL orders viewed in the
    // Processor Orders dashboard.
    //
    // Every account that lands on `/dashboard/processor-orders` is
    // already a processor-type role (Search Processor, Tax Processor,
    // Title Search Processor) — their list is filtered server-side to
    // their own queue. Bulk-uploaded orders carry product types that
    // weren't in the original popup whitelist (e.g. "Recon", "NTC",
    // "Lien Release", "Image Sweep") and previously fell through to
    // a navigation. That created a UX split between manually-created
    // and bulk-uploaded orders. Per product spec, both paths must be
    // visually identical.
    //
    // Resolution: open the OrderSummaryPopup MODAL for every click on
    // this dashboard, regardless of product type. Team Leaders also
    // routed here open the same modal. Order Desk and other roles do
    // NOT use this dashboard, so they're unaffected.
    const role = (viewRole || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
    const isTeamLeaderRole = role === 'title_search_team_leader' || role === 'tax_search_team_leader' || role === 'team_leader';
    const shouldOpenPopup = isProcessorRole(user?.role) || isTeamLeaderRole || role === 'admin';

    if (shouldOpenPopup) {
      const orderDataForPopup = {
        ...order,
        client_product: order.clientProduct || order.client_product,
      };

      // Feb 2026 — DELIVERY-EDIT-RBAC-001 — Surface an "Edit Order" CTA
      // for Admin + Team Leaders. TLs are restricted to in-team orders;
      // out-of-team rows fall through to the existing read-only popup.
      const orderIsTax = isTaxProduct(orderDataForPopup);
      let canEdit = false;
      if (role === 'admin' || role === 'team_leader') {
        canEdit = true;
      } else if (role === 'tax_search_team_leader') {
        canEdit = orderIsTax;
      } else if (role === 'title_search_team_leader') {
        canEdit = !orderIsTax;
      }

      setOrderSummaryPopup({
        isOpen: true,
        orderData: orderDataForPopup,
        showEditOrderButton: canEdit,
        onEditOrder: canEdit ? () => {
          setOrderSummaryPopup({ isOpen: false, orderData: null });
          setEditModalOrder(orderDataForPopup);
          setShowEditModal(true);
        } : undefined,
      });
      return;
    }

    // Fallback for any role not covered above (Order Desk, etc.).
    // Inline Summary tab still renders with the popup-mirror design.
    navigate(`/orders/${orderId}/summary`);
  };

  // Handle status update
  const handleStatusUpdate = async (orderId, newStatus, reason = '', note = '') => {
    try {
      const payload = { status: newStatus };
      
      if (newStatus === 'pending' || newStatus === 'clarification') {
        if (reason) payload.reason = reason;
      } else if (note) {
        payload.note = note;
      }
      
      await axios.patch(`${API}/orders/${orderId}/status`, payload);
      
      setOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId ? { ...order, status: newStatus } : order
        )
      );
      
      toast.success(`Order status updated to ${newStatus}. Redirecting to dashboard...`, { duration: 1500 });
      
      // GLOBAL RULE: Redirect to dashboard after ANY successful status update
      // This applies to all Processor roles (Tax Processor, Title Processor, Search Processor)
      // Prevents stale UI, duplicate actions, and ensures fresh dashboard data
      console.log(`[PROCESSOR] Status changed to ${newStatus}. Redirecting to dashboard in 1.5 seconds...`);
      setTimeout(() => {
        console.log('[PROCESSOR] Executing redirect to dashboard NOW');
        navigate('/dashboard');
      }, 1500);
      
    } catch (error) {
      console.error('Error updating order status:', error);
      
      if (error.response?.status === 403) {
        toast.error('You can only update orders assigned to you');
      } else if (error.response?.status === 400) {
        toast.error(error.response?.data?.detail || 'Invalid status transition');
      } else if (error.response?.status === 422) {
        const errors = error.response?.data?.detail;
        toast.error(Array.isArray(errors) && errors.length > 0 ? errors[0].msg : 'Validation error');
      } else {
        toast.error('Failed to update order status');
      }
      
      await fetchOrders();
    }
  };

  // Processor Status Control Component
  const ProcessorStatusControl = ({ order }) => {
    const [showReasonModal, setShowReasonModal] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState('');
    const [reason, setReason] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const allowedTransitions = getAllowedProcessorTransitions(order.status);
    const availableOptions = processorStatusOptions.filter(option => 
      allowedTransitions.includes(option.value)
    );

    const handleStatusChange = (newStatus) => {
      if (newStatus === 'pending' || newStatus === 'clarification') {
        setSelectedStatus(newStatus);
        setReason('');
        setShowReasonModal(true);
      } else {
        handleStatusUpdate(order.id, newStatus);
      }
    };

    const handleReasonSubmit = async () => {
      if (!reason.trim()) {
        toast.error('Reason is required');
        return;
      }
      if (reason.trim().length < 10) {
        toast.error('Reason must be at least 10 characters long');
        return;
      }
      if (reason.length > 500) {
        toast.error('Reason cannot exceed 500 characters');
        return;
      }

      setIsSubmitting(true);
      try {
        await handleStatusUpdate(order.id, selectedStatus, reason.trim());
        setShowReasonModal(false);
        setReason('');
        setSelectedStatus('');
      } finally {
        setIsSubmitting(false);
      }
    };

    const handleModalClose = () => {
      setShowReasonModal(false);
      setReason('');
      setSelectedStatus('');
    };

    if (availableOptions.length === 0) {
      return <StatusBadge status={order.status} userRole={viewRole} orderData={order} />;
    }

    return (
      <>
        <Select value={order.status} onValueChange={handleStatusChange}>
          <SelectTrigger className="w-32 h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={order.status} disabled>
              <StatusBadge status={order.status} userRole={viewRole} orderData={order} />
            </SelectItem>
            {availableOptions.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Dialog open={showReasonModal} onOpenChange={setShowReasonModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>
                Reason Required - {selectedStatus === 'pending' ? 'Pending' : 'Clarification'}
              </DialogTitle>
              <DialogDescription>
                Please provide a reason for changing the status to {selectedStatus === 'pending' ? 'Pending' : 'Clarification'}.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <Label htmlFor="reason">Reason *</Label>
                <textarea
                  id="reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Enter reason (minimum 10 characters)..."
                  className="w-full mt-1 p-2 border border-gray-300 rounded-md resize-none h-24 text-sm"
                  maxLength={500}
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-gray-500">Minimum 10 characters required</span>
                  <span className={`text-xs ${reason.length > 450 ? 'text-red-500' : 'text-gray-500'}`}>
                    {reason.length}/500
                  </span>
                </div>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={handleModalClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button 
                onClick={handleReasonSubmit} 
                disabled={!reason.trim() || reason.trim().length < 10 || isSubmitting}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {isSubmitting ? 'Updating...' : `Update to ${selectedStatus === 'pending' ? 'Pending' : 'Clarification'}`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </>
    );
  };

  return (
    <div className="space-y-3" data-testid="processor-orders-page">
      <OpsIntroOverlay />
      {/* Dashboard Stats Cards */}
      <ProcessorKPICards stats={dashboardStats || {}} userRole={viewRole} loading={dashboardStats === null} />
      
      {/* Performance Strip - Inline Performance Awareness */}
      {/* OPS-DASH-001 — Operational Productivity Dashboard (replaces PerformanceStrip) */}
      <OperationalDashboard userId={user?.id} onFilterStatus={(s) => {
        if (statusTabs.some(t => t.value === s)) { setActiveTab(s); setCurrentPage(1); }
      }} />
      
      {/* DASH-COMPACT-001: unified control panel (search + scope filters + MLS state + legend) */}
      <div className="rounded-2xl border border-blue-100 bg-gradient-to-br from-sky-50/90 via-white to-blue-50/70 shadow-[0_8px_24px_rgba(37,99,235,0.08)] px-4 pt-3.5 pb-3" data-testid="dashboard-control-panel">
          <div className="flex flex-wrap gap-3 items-center">
            <div className="flex-1 relative">
              <Input
                placeholder="Search by Order #, Product ID, Client Code, Customer, State, or Address..."
                value={searchInputDisplay}
                onChange={(e) => setSearchInputDisplay(e.target.value)}
                className="w-full h-9 pr-10 rounded-lg border-slate-200 bg-white text-sm shadow-sm placeholder:text-slate-400 hover:border-slate-300 focus-visible:ring-2 focus-visible:ring-blue-300"
                data-testid="search-input"
              />
              {/* Inline loading indicator during search */}
              {isRefreshing && searchInputDisplay && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                </div>
              )}
              {/* Clear button when search has text */}
              {searchInputDisplay && !isRefreshing && (
                <button
                  onClick={() => clearSearchState()}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  data-testid="clear-search"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            
            {/* ORDER-FILTERS-001: Client Code / Product (assigned/workstream scope only, display filter) */}
            <OrderScopeFilters
              clientCode={scopeFilters.client_code}
              clientProduct={scopeFilters.client_product}
              onChange={handleScopeFilterChange}
              options={scopeFacets}
            />

            {/* Priority Legend */}
            <div className="flex items-center space-x-4 text-sm text-gray-600">
              <div className="flex items-center space-x-1">
                <div className="bg-red-500 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold">!</div>
                <span>Urgent</span>
              </div>
              <div className="flex items-center space-x-1">
                <div className="bg-blue-500 w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold">R</div>
                <span>Rush</span>
              </div>
            </div>
          </div>
          {/* ORDER-FILTERS-001: active scope chips + Clear All */}
          <div className="mt-2 empty:hidden">
            <OrderScopeChips
              clientCode={scopeFilters.client_code}
              clientProduct={scopeFilters.client_product}
              onChange={handleScopeFilterChange}
            />
          </div>
      </div>

      {/* Orders Card — status filters directly above the table */}
      <Card className="rounded-xl border-slate-200 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 px-4 pt-3 pb-3">
          <CardTitle className="flex items-center gap-2">
            {/* Status Filter Tabs */}
            <div className="flex gap-2 flex-wrap">
              {statusTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => {
                    setActiveTab(tab.value);
                    setCurrentPage(1);
                  }}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeTab === tab.value 
                      ? `${tab.color} bg-blue-50 ring-2 ring-offset-1 ring-blue-500` 
                      : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                  }`}
                  data-testid={`status-tab-${tab.value}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </CardTitle>
        </CardHeader>
        
        
        {/* Orders Table */}
        <CardContent className="p-0">
          {/* DASH-COMPACT-001: standalone "Orders" label removed — table starts right under the status tabs */}
          <div className="border-t" />
          <div className="table-container">
            {loading && orders.length === 0 ? (
              <div className="flex items-center justify-center p-8">
                <div className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  <span>Loading orders...</span>
                </div>
              </div>
            ) : (
              <Table ref={processorTableRef} className="orders-table resizable-table w-full table-fixed">
                <colgroup>
                  {visibleProcessorColumns.map((column) => (
                    <col key={column.id} style={{ width: column.width }} />
                  ))}
                </colgroup>
                <TableHeader>
                  <TableRow>
                    {visibleProcessorColumns.map((column) => {
                      if (column.conditional && !features[column.conditional]) {
                        return null;
                      }
                      
                      // Map column IDs to backend sort keys.
                      // NOTE (May 6, 2026 — sort regression fix): the
                      // 'Order Date' column was inserted on Feb 25, 2026
                      // with id `order_date`, but this map kept the
                      // legacy `ordered_on` key from a prior column
                      // shape. Looking up `sortKeyMap['order_date']`
                      // returned `undefined` → `isSortable=false` →
                      // `onSort=undefined` → the header was no longer
                      // clickable for Search Processor / Tax Processor
                      // (other dashboards use their own column wiring,
                      // which is why Admin/TL were unaffected). Adding
                      // the canonical `order_date → orderedOn` entry
                      // fixes it; the legacy `ordered_on` row is kept
                      // as a defensive alias in case any other caller
                      // still references it.
                      const sortKeyMap = {
                        'order_number': 'orderNumber',
                        'status': 'status',
                        'client_product': 'clientProduct',
                        'product_id': 'productId',
                        'client_code': 'clientCode',
                        'state': 'state',
                        'county': 'county',
                        'order_date': 'orderedOn',   // canonical — matches column id at line 145
                        'ordered_on': 'orderedOn',   // legacy alias (defensive)
                        'due_by': 'dueBy',
                        'assignee': 'assignee',
                        'borrower_name': 'borrowerName',
                        'property_address': 'propertyAddress'
                      };
                      
                      const sortKey = sortKeyMap[column.id];
                      const isSortable = column.sortable !== false && sortKey;
                      
                      return (
                        <ResizableTableHead
                          key={column.id}
                          column={{ ...column, sortable: isSortable }}
                          onResizeStart={handleProcessorResizeStart}
                          onAutoFit={handleProcessorAutoFit}
                          onKeyboardResize={handleProcessorKeyboardResize}
                          isResizing={isProcessorTableResizing && resizingProcessorColumn === column.id}
                          sortDirection={sortKey && sortBy === sortKey ? sortDir : null}
                          onSort={isSortable ? () => handleSort(sortKey) : undefined}
                        >
                          {column.title}
                        </ResizableTableHead>
                      );
                    })}
                  </TableRow>
                </TableHeader>
                <TableBody>
                 {orders.map((order) => {
                    const getCellContent = (columnId) => {
                      switch (columnId) {
                        case 'order_number':
                          return (
                            <TableCell className="font-medium whitespace-nowrap">
                                <button 
                                  className={`hover:text-blue-800 hover:underline transition-colors ${['rush', 'urgent'].includes(order.priority?.toLowerCase()) ? 'text-red-600 font-bold' : 'text-blue-600'}`}
                                  onClick={() => handleOrderClick(order.id)}
                                  data-testid={`order-link-${order.orderNumber}`}
                                >
                                  {order.orderNumber}
                                </button>
                            </TableCell>
                          );
                        case 'order_date': {
                          // Order Date column — Feb 25, 2026.
                          // Sources from `created_at` which is mapped to `orderedOn`
                          // by the processor list mapper (ProcessorOrderItem model).
                          const raw = order.orderedOn || order.createdAt || order.created_at || order.receivedDate || order.received_date;
                          return (
                            <TableCell className="whitespace-nowrap">
                              {raw
                                ? <span className="text-sm text-slate-700 tabular-nums">{formatDateOnlyET(raw)}</span>
                                : <span className="text-slate-400">—</span>}
                            </TableCell>
                          );
                        }
                        case 'client_code':
                          return (
                            <TableCell>
                              <Badge variant="outline" className="text-xs font-mono" title={`Full code: ${order.clientCode || 'Unknown'}`}>
                                {order.clientShortCode || order.clientCode || 'N/A'}
                              </Badge>
                            </TableCell>
                          );
                        case 'client_product':
                          {
                            // Feb 22, 2026 — Search Processor visibility parity with Admin/TL.
                            // Bug: Processor only saw `RECON` while Admin/TL saw `RECON - FM001`.
                            // Root cause: ProcessorOrderItem model didn't expose `contract_code`
                            // and this cell didn't render it. Fix: render the same "product -
                            // contract" pattern used by OrdersTable.jsx L317–343 so all roles
                            // see the same display.
                            const product = order.clientProduct || 'N/A';
                            const contract = order.contractCode;
                            return (
                              <TableCell
                                title={productDisplayText(product, contract)}
                                data-testid={`processor-product-${order.id}`}
                              >
                                <ProductWithCode
                                  product={product}
                                  code={contract}
                                  codeTestId={`processor-contract-code-${order.id}`}
                                  className="max-w-[220px]"
                                />
                              </TableCell>
                            );
                          }
                        case 'borrower_name':
                          return (
                            <TableCell>
                              <div className="max-w-[150px] truncate" title={normalizeBorrowerName(order.borrowerName || order.customer) || (order.borrowerName || order.customer)}>
                                {normalizeBorrowerName(order.borrowerName || order.customer) || 'N/A'}
                              </div>
                            </TableCell>
                          );
                        case 'property_address': {
                          const fullAddr = formatFullAddress({
                            street: order.propertyAddress,
                            city:   order.city,
                            state:  order.state,
                            zip:    order.zip,
                          });
                          return (
                            <TableCell style={{ paddingLeft: '8px', paddingRight: '8px' }}>
                              <div className="truncate" title={fullAddr}>
                                {fullAddr}
                              </div>
                            </TableCell>
                          );
                        }
                        case 'county':
                          return <TableCell>{formatCounty(order.county)}</TableCell>;
                        case 'state':
                          return <TableCell>{formatState(order.state)}</TableCell>;
                        case 'assignee':
                          // QC-ASSIGNEE-001: in QC Review show the QC claimant; Search Processor kept in title.
                          if (order.status === 'qc_review' && order.qcAssigneeName) {
                            return (
                              <TableCell title={`Search Processor: ${order.assigneeName || order.assignee || 'Unassigned'}`} data-testid={`tl-assignee-qc-${order.id}`}>
                                {order.qcAssigneeName}
                                <span className="ml-1.5 rounded bg-fuchsia-100 px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-fuchsia-700">QC</span>
                              </TableCell>
                            );
                          }
                          return <TableCell>{order.assigneeName || order.assignee || 'Unassigned'}</TableCell>;
                        case 'status':
                          return (
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {order.mlsStatus ? (
                                  <MlsDualStatusBadges order={{ ...order, mls_status: order.mlsStatus }} />
                                ) : (
                                  <StatusBadge status={order.status} userRole={viewRole} orderData={order} />
                                )}
                                <FaxIndicator faxStatus={order.fax_status} faxDeliveries={order.fax_deliveries} />
                                <EmailIndicator taxEmailStatus={order.tax_email_status} taxEmailRequests={order.tax_email_requests} />
                                <ClarificationSentIndicator order={order} />
                                <PriorityIndicator priority={order.priority} />
                              </div>
                            </TableCell>
                          );
                        default:
                          return null;
                      }
                    };
                
                    return (
                      <TableRow 
                        key={order.id || order.orderNumber} 
                        className={`hover:bg-gray-50 ${
                          order.priority?.toLowerCase() === 'urgent' ? 'bg-red-50 border-l-4 border-l-red-500' :
                          order.priority?.toLowerCase() === 'rush' ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                        }`}
                        data-testid={`order-row-${order.orderNumber}`}
                      >
                        {visibleProcessorColumns.map((column) => {
                          if (column.conditional && !features[column.conditional]) {
                            return null;
                          }
                          return (
                            <React.Fragment key={column.id}>
                              {getCellContent(column.id)}
                            </React.Fragment>
                          );
                        })}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}

            {orders.length === 0 && !loading && (
              <div className="text-center py-12">
                <div className="flex flex-col items-center gap-3">
                  <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <div>
                    <p className="text-lg font-medium text-gray-900 mb-1">No orders found</p>
                    <p className="text-sm text-gray-500">
                      {searchQuery ? (
                        <>No orders match your search: <span className="font-medium">&quot;{searchQuery}&quot;</span></>
                      ) : (
                        <>No orders found for the selected filters and tab.</>
                      )}
                    </p>
                    {searchQuery && (
                      <button
                        onClick={() => clearSearchState()}
                        className="mt-3 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                      >
                        Clear search
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          
          <ResizeGuide isResizing={isProcessorTableResizing} />
        </CardContent>

        {/* Pagination */}
        {totalOrders > pageSize && (
          <CardContent className="border-t">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-600">
                Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalOrders)} of {totalOrders} orders
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1 || loading}
                  data-testid="pagination-prev"
                >
                  Previous
                </Button>
                <span className="text-sm px-3 py-1 bg-gray-100 rounded">
                  Page {currentPage} of {Math.ceil(totalOrders / pageSize)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={currentPage >= Math.ceil(totalOrders / pageSize) || loading}
                  data-testid="pagination-next"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Order Summary Popup */}
      <OrderSummaryPopup
        {...orderSummaryPopup}
        currentUser={user}
        onClose={() => setOrderSummaryPopup({ isOpen: false, orderData: null })}
        onUpdate={() => fetchOrders(true)}
      />

      {/* Feb 2026 — DELIVERY-EDIT-RBAC-001 — Order Edit Modal for Admin / TLs */}
      {['admin', 'tax_search_team_leader', 'title_search_team_leader', 'team_leader'].includes(
        (user?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
      ) && editModalOrder && (
        <OrderDeskEditModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditModalOrder(null);
          }}
          order={editModalOrder}
          onUpdate={async (orderId, updateData) => {
            try {
              await axios.put(`${API}/orders-v2/${orderId}`, updateData);
              toast.success('Order updated successfully');
              fetchOrders(true);
              return true;
            } catch (err) {
              const detail = err?.response?.data?.detail || err?.message || 'Update failed';
              toast.error(typeof detail === 'string' ? detail : 'Update failed');
              return false;
            }
          }}
        />
      )}

      {/* Scroll to Top Button - appears on long pages */}
      <ScrollToTopButton />
    </div>
  );
};

export default ProcessorOrders;
